/**
 * Contratos (FHIR Contract) - helpers de leitura e regras de negócio.
 *
 * Motivação:
 * - Contratos gratuitos podem não possuir `nextPaymentDueDate`.
 * - A UI precisa distinguir "plano gratuito" (com upgrade opcional) de contratos pagos.
 * - O limite de profissionais é usado em múltiplas telas; centralizar evita divergências.
 */
type ContractLike =
  | {
      extension?: any[];
      identifier?: any[];
      term?: any[];
    }
  | undefined;

const EXTENSION_URLS = {
  subscriptionType:
    "https://fluxmed.com.br/hapi/fhir/R5/extensions/subscriptionType",
  externalReference:
    "https://fluxmed.com.br/hapi/fhir/R5/extensions/externalReference",
} as const;

export function getContractExtensionValueString(
  contract: ContractLike,
  urlIncludes: string
): string | undefined {
  const ext = contract?.extension?.find((el: any) =>
    String(el?.url || "").includes(urlIncludes)
  );
  return ext?.valueString;
}

export function getExternalReference(contract: ContractLike): string | undefined {
  // `externalReference` hoje é usado como referência externa do plano
  // (ex.: 1 => plano gratuito CPF, 3 => plano gratuito organização).
  return getContractExtensionValueString(
    contract,
    EXTENSION_URLS.externalReference
  );
}

export function getSubscriptionType(contract: ContractLike): string | undefined {
  return getContractExtensionValueString(contract, EXTENSION_URLS.subscriptionType);
}

export function getMaxPractitioners(contract: ContractLike): number | undefined {
  const raw = contract?.term?.[0]?.identifier?.value;
  if (raw === undefined || raw === null || raw === "") return undefined;
  const value = Number(raw);
  return Number.isFinite(value) ? value : undefined;
}

export function getNextPaymentDueDate(contract: ContractLike): string | undefined {
  const ext = contract?.extension?.find((el: any) =>
    String(el?.url || "").includes("nextPaymentDueDate")
  );

  // A API pode retornar `valueDateTime` ou `valueDate`.
  return (ext as any)?.valueDateTime || (ext as any)?.valueDate;
}

export function isFluxPayContract(contract: ContractLike): boolean {
  return (
    contract?.identifier?.some(
      (identifier: any) =>
        identifier.system?.includes("contract-type") &&
        identifier.value === "fluxPayPlan"
    ) || false
  );
}

export function isFreePlanContract(contract: ContractLike): boolean {
  // Regra solicitada: se não existir contrato ativo, o sistema deve tratar como plano gratuito.
  if (!contract) return true;

  // Regra principal (explícita): contratos gratuitos têm `externalReference` conhecido.
  const externalReference = getExternalReference(contract);
  if (externalReference === "1" || externalReference === "3") return true;

  // Fallback (legado): alguns contratos podem sinalizar o tipo via `subscriptionType`.
  const subscriptionType = getSubscriptionType(contract) || "";
  if (subscriptionType.toLowerCase().includes("acesso gratuito")) return true;

  // Fallback adicional: caso o contrato esteja com valor unitário zero.
  // (Não usamos como regra principal para evitar falsos positivos em cenários futuros.)
  const unitPriceValue = (contract as any)?.term?.[0]?.asset?.[0]?.valuedItem?.[0]
    ?.unitPrice?.value;
  if (unitPriceValue === 0) return true;

  return false;
}
